/**
 * Bulk instant apply — Promise.allSettled over the selected rows, per-row
 * outcomes surfaced (never all-or-nothing), one invalidation sweep after.
 * Only library trackers (Voluum, Binom) are applied to: a row of any other
 * tracker without the optimizer capability is reported as a failure without a request.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  trackerLabel,
  type FlowOptimizerLibraryProvider,
  type FlowOptimizerPackageApplyResult,
} from '@trafficpulse/shared';

import { QK } from '@/features/flow-optimizer/hooks/useFlowOptimizerConfig';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';
import { libraryProviderOf } from '@/features/trackers/flow-optimizer/library-provider';

import { applyPackage } from '../api/client/packages-api';
import {
  cachedPackageColor,
  invalidateAfterApply,
  patchRowPackage,
} from './useAssignPackage';

export interface BulkAssignOutcome {
  ok: number;
  total: number;
  failures: Array<{ campaignName: string; reason: string }>;
}

interface LibraryTarget {
  row: OptimizerRow;
  provider: FlowOptimizerLibraryProvider;
}

/** Selected rows split into those the library can apply to and the rest. */
export function splitLibraryTargets(rows: OptimizerRow[]): {
  library: LibraryTarget[];
  skipped: OptimizerRow[];
} {
  const library: LibraryTarget[] = [];
  const skipped: OptimizerRow[] = [];
  for (const row of rows) {
    const provider = libraryProviderOf(row.provider);
    if (provider) library.push({ row, provider });
    else skipped.push(row);
  }
  return { library, skipped };
}

/** Seed the config cache + the list row with the applied package. */
function seedApplied(
  queryClient: QueryClient,
  { row, provider }: LibraryTarget,
  result: FlowOptimizerPackageApplyResult,
): void {
  queryClient.setQueryData(QK.config(provider, row.campaignId), result.config);
  patchRowPackage(queryClient, { provider, campaignId: row.campaignId }, {
    id: result.packageId,
    name: result.packageName,
    color: cachedPackageColor(queryClient, result.packageId),
  });
}

const failureReason = (reason: unknown): string =>
  reason instanceof Error ? reason.message : 'Apply failed';

async function applyAll(
  queryClient: QueryClient,
  input: { packageId: string; targets: OptimizerRow[] },
): Promise<BulkAssignOutcome> {
  const { library, skipped } = splitLibraryTargets(input.targets);
  const settled = await Promise.allSettled(
    library.map(({ row, provider }) =>
      applyPackage(input.packageId, { provider, campaignId: row.campaignId }),
    ),
  );
  const failures: BulkAssignOutcome['failures'] = [];
  settled.forEach((result, index) => {
    const target = library[index];
    if (result.status === 'fulfilled') return seedApplied(queryClient, target, result.value);
    failures.push({ campaignName: target.row.campaignName, reason: failureReason(result.reason) });
  });
  const ok = settled.length - failures.length;
  for (const row of skipped) {
    const reason = `Packages are not available for ${trackerLabel(row.provider)}`;
    failures.push({ campaignName: row.campaignName, reason });
  }
  return { ok, total: input.targets.length, failures };
}

export function useBulkAssign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { packageId: string; targets: OptimizerRow[] }) =>
      applyAll(queryClient, input),
    onSettled: (_outcome, _error, input) => {
      const { library } = splitLibraryTargets(input.targets);
      const providers = new Set(library.map((target) => target.provider));
      for (const provider of providers) {
        invalidateAfterApply(queryClient, {
          provider,
          campaignId: library[0]?.row.campaignId ?? '',
        });
      }
    },
  });
}
